let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  if (audioCtx.state === "suspended") void audioCtx.resume();
  return audioCtx;
}

/**
 * Builds a mono noise buffer. With `crackle` the noise is gated into short
 * random spikes, which reads as the sputtering of an igniting engine.
 */
function makeNoiseBuffer(
  ac: AudioContext,
  seconds: number,
  crackle = false
): AudioBuffer {
  const frames = Math.max(1, Math.floor(ac.sampleRate * seconds));
  const buffer = ac.createBuffer(1, frames, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    const sample = Math.random() * 2 - 1;
    data[i] = crackle ? sample * (Math.random() > 0.994 ? 1 : 0.07) : sample;
  }
  return buffer;
}

/**
 * Full rocket launch (~2 s): ignition crackle, a deep rumble that keeps
 * shuddering, a wide noise sweep as the rocket climbs, and a rising whistle
 * that drifts off to the side as it fades into the distance.
 * Fully synthesized - no audio files needed.
 */
export function playLaunchSound(): void {
  try {
    const ac = getAudioContext();
    if (!ac) return;

    const now = ac.currentTime;
    const total = 2;
    const out = ac.destination;

    const panner =
      typeof ac.createStereoPanner === "function" ? ac.createStereoPanner() : null;
    if (panner) {
      panner.pan.setValueAtTime(-0.22, now);
      panner.pan.linearRampToValueAtTime(0.28, now + total);
      panner.connect(out);
    }
    const bus = panner ?? out;

    // --- 1. ignition crackle: gated noise, the engine catching ---
    const crackleSrc = ac.createBufferSource();
    crackleSrc.buffer = makeNoiseBuffer(ac, 1.1, true);

    const crackleFilter = ac.createBiquadFilter();
    crackleFilter.type = "bandpass";
    crackleFilter.Q.value = 0.9;
    crackleFilter.frequency.setValueAtTime(1100, now);
    crackleFilter.frequency.exponentialRampToValueAtTime(2600, now + 0.9);

    const crackleGain = ac.createGain();
    crackleGain.gain.setValueAtTime(0.0001, now);
    crackleGain.gain.exponentialRampToValueAtTime(0.075, now + 0.07);
    crackleGain.gain.exponentialRampToValueAtTime(0.05, now + 0.55);
    crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);

    crackleSrc.connect(crackleFilter);
    crackleFilter.connect(crackleGain);
    crackleGain.connect(bus);
    crackleSrc.start(now);
    crackleSrc.stop(now + 1.1);

    // --- 2. thump: the low kick of ignition ---
    const thump = ac.createOscillator();
    const thumpGain = ac.createGain();
    thump.type = "sine";
    thump.frequency.setValueAtTime(155, now);
    thump.frequency.exponentialRampToValueAtTime(44, now + 0.45);
    thumpGain.gain.setValueAtTime(0.0001, now);
    thumpGain.gain.exponentialRampToValueAtTime(0.17, now + 0.02);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);

    thump.connect(thumpGain);
    thumpGain.connect(bus);
    thump.start(now);
    thump.stop(now + 0.62);

    // --- 3. rumble: low noise bed with a shudder on top ---
    const rumbleSrc = ac.createBufferSource();
    rumbleSrc.buffer = makeNoiseBuffer(ac, total);

    const rumbleFilter = ac.createBiquadFilter();
    rumbleFilter.type = "lowpass";
    rumbleFilter.frequency.setValueAtTime(260, now);
    rumbleFilter.frequency.linearRampToValueAtTime(150, now + total);
    rumbleFilter.Q.value = 0.7;

    const rumbleGain = ac.createGain();
    rumbleGain.gain.setValueAtTime(0.0001, now);
    rumbleGain.gain.exponentialRampToValueAtTime(0.085, now + 0.25);
    rumbleGain.gain.exponentialRampToValueAtTime(0.045, now + 1.1);
    rumbleGain.gain.exponentialRampToValueAtTime(0.0001, now + total);

    // slow tremolo so the rumble feels mechanical, not like a hiss
    const shudder = ac.createOscillator();
    const shudderDepth = ac.createGain();
    shudder.type = "sine";
    shudder.frequency.setValueAtTime(9, now);
    shudder.frequency.linearRampToValueAtTime(14, now + total);
    shudderDepth.gain.value = 0.022;
    shudder.connect(shudderDepth);
    shudderDepth.connect(rumbleGain.gain);
    shudder.start(now);
    shudder.stop(now + total);

    rumbleSrc.connect(rumbleFilter);
    rumbleFilter.connect(rumbleGain);
    rumbleGain.connect(bus);
    rumbleSrc.start(now);
    rumbleSrc.stop(now + total);

    // --- 4. whoosh: the climb, sweeping up and then past you ---
    const whooshSrc = ac.createBufferSource();
    whooshSrc.buffer = makeNoiseBuffer(ac, total);

    const whooshFilter = ac.createBiquadFilter();
    whooshFilter.type = "bandpass";
    whooshFilter.Q.value = 1.1;
    whooshFilter.frequency.setValueAtTime(240, now);
    whooshFilter.frequency.exponentialRampToValueAtTime(620, now + 0.45);
    whooshFilter.frequency.exponentialRampToValueAtTime(2300, now + 1.25);
    whooshFilter.frequency.exponentialRampToValueAtTime(520, now + total);

    const whooshGain = ac.createGain();
    whooshGain.gain.setValueAtTime(0.0001, now);
    whooshGain.gain.exponentialRampToValueAtTime(0.1, now + 0.18);
    whooshGain.gain.exponentialRampToValueAtTime(0.09, now + 1.1);
    whooshGain.gain.exponentialRampToValueAtTime(0.0001, now + total);

    whooshSrc.connect(whooshFilter);
    whooshFilter.connect(whooshGain);
    whooshGain.connect(bus);
    whooshSrc.start(now);
    whooshSrc.stop(now + total);

    // --- 5. whistle: rises while drifting off to the side ---
    const whistle = ac.createOscillator();
    const whistleGain = ac.createGain();
    whistle.type = "triangle";
    whistle.frequency.setValueAtTime(430, now + 0.35);
    whistle.frequency.exponentialRampToValueAtTime(1180, now + 1.15);
    whistle.frequency.exponentialRampToValueAtTime(1560, now + total);
    whistleGain.gain.setValueAtTime(0.0001, now + 0.35);
    whistleGain.gain.exponentialRampToValueAtTime(0.035, now + 0.75);
    whistleGain.gain.exponentialRampToValueAtTime(0.0001, now + total);

    whistle.connect(whistleGain);
    whistleGain.connect(bus);
    whistle.start(now + 0.35);
    whistle.stop(now + total + 0.02);

    // --- 6. tail sparkle: last little sputter as it disappears ---
    const tail = ac.createBufferSource();
    tail.buffer = makeNoiseBuffer(ac, 0.35, true);

    const tailFilter = ac.createBiquadFilter();
    tailFilter.type = "highpass";
    tailFilter.frequency.value = 1800;

    const tailGain = ac.createGain();
    tailGain.gain.setValueAtTime(0.0001, now + 1.55);
    tailGain.gain.exponentialRampToValueAtTime(0.03, now + 1.65);
    tailGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.9);

    tail.connect(tailFilter);
    tailFilter.connect(tailGain);
    tailGain.connect(bus);
    tail.start(now + 1.55);
    tail.stop(now + 1.9);
  } catch {
    // audio is a nice-to-have, never break the click
  }
}

/**
 * Short three-beat vibration: ignition, lift-off, fade out.
 * Silently ignored where unsupported (e.g. iOS Safari).
 */
export function vibrateLaunch(): void {
  try {
    if (typeof navigator === "undefined" || !navigator.vibrate) return;
    navigator.vibrate([14, 45, 26, 40, 40]);
  } catch {
    // ignore
  }
}